import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Header from '../components/layout/Header'
import TvlFilterMenu from '../components/staking/TvlFilterMenu'
import { startTvlChartInteraction } from '../components/staking/tvlChartInteraction'
import {
  positionsEmptyHtml,
  startPositionsSignal,
  stopPositionsSignal,
} from '../components/trade/positionsEmpty'
import StakingMarkup from '../components/staking/generated/StakingMarkup'
import mobileTvlChartHtml from '../components/staking/generated/tvl-chart-mobile.html?raw'
import { usePageCss } from '../hooks/usePageCss'
import './staking-mobile.css'

let desktopTvlSvgHtml = null
let stakingTvlChartMode = null

const TAB_ACTIVE =
  "relative flex items-center justify-center gap-1 text-sm/sm whitespace-nowrap transition-colors rounded-card font-medium text-ink shrink-0 px-2.5 py-1 max-mobile:before:absolute max-mobile:before:inset-x-0 max-mobile:before:-inset-y-2 max-mobile:before:content-['']"
const TAB_IDLE =
  "relative flex items-center justify-center gap-1 text-sm/sm whitespace-nowrap transition-colors rounded-card font-normal hover:text-ink active:text-ink shrink-0 hover:bg-ink/5 px-2.5 py-1 text-dim max-mobile:before:absolute max-mobile:before:inset-x-0 max-mobile:before:-inset-y-2 max-mobile:before:content-['']"
const TAB_PILL =
  'absolute inset-0 rounded-card bg-fill shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under)] light:border-transparent light:bg-surface light:shadow-[0_0_0_0.5px_var(--ring-crisp),0_1px_0_0_var(--edge-under)] light:shadow-[inset_0_0_0_0.5px_var(--ring-crisp)]!'

const BOTTOM_PANEL_CLASS =
  'flex flex-col gap-2 max-mobile:min-h-100 min-h-[max(400px,calc(100vh-560px))]'
const BOTTOM_PANEL_FEE_CLASS = 'flex flex-col gap-2'

function feeInfoIcon(clipId) {
  return `<div class="relative before:absolute before:-inset-2 before:content-[''] max-mobile:before:-inset-[10px]" data-state="closed"><span data-icon="true" class="flex shrink-0 items-center justify-center size-3.5 text-dim"><svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" class="size-full"><g clip-path="url(#${clipId})"><path d="M6 10.5C8.48528 10.5 10.5 8.48528 10.5 6C10.5 3.51472 8.48528 1.5 6 1.5C3.51472 1.5 1.5 3.51472 1.5 6C1.5 8.48528 3.51472 10.5 6 10.5Z" stroke="currentColor" stroke-opacity="0.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M5.625 5.625C5.72446 5.625 5.81984 5.66451 5.89016 5.73484C5.96049 5.80516 6 5.90054 6 6V7.875C6 7.97446 6.03951 8.06984 6.10983 8.14017C6.18016 8.21049 6.27554 8.25 6.375 8.25" stroke="currentColor" stroke-opacity="0.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M5.8125 4.5C6.12316 4.5 6.375 4.24816 6.375 3.9375C6.375 3.62684 6.12316 3.375 5.8125 3.375C5.50184 3.375 5.25 3.62684 5.25 3.9375C5.25 4.24816 5.50184 4.5 5.8125 4.5Z" fill="currentColor" fill-opacity="0.5"></path></g><defs><clipPath id="${clipId}"><rect width="12" height="12" fill="white"></rect></clipPath></defs></svg></span></div>`
}

const EXPLORER_ICON = `<span data-icon="true" class="flex shrink-0 items-center justify-center size-[13px]"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" class="size-full"><g><path d="M2.04297 4.80762L7.99922 8.06762L13.9555 4.80762" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path><path d="M8.24 1.56234L13.74 4.57359C13.8185 4.61656 13.8841 4.67983 13.9298 4.75679C13.9756 4.83375 13.9998 4.92157 14 5.01109V10.9886C13.9998 11.0781 13.9756 11.1659 13.9298 11.2429C13.8841 11.3199 13.8185 11.3831 13.74 11.4261L8.24 14.4373C8.16641 14.4776 8.08388 14.4987 8 14.4987C7.91612 14.4987 7.83359 14.4776 7.76 14.4373L2.26 11.4261C2.18147 11.3831 2.11591 11.3199 2.07017 11.2429C2.02444 11.1659 2.0002 11.0781 2 10.9886V5.01109C2.0002 4.92157 2.02444 4.83375 2.07017 4.75679C2.11591 4.67983 2.18147 4.61656 2.26 4.57359L7.76 1.56234C7.83359 1.52208 7.91612 1.50098 8 1.50098C8.08388 1.50098 8.16641 1.52208 8.24 1.56234Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path><path d="M8 8.06836V14.5002" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path></g></svg></span>`

const STAKING_POOL = '281474976624800 (Staking Pool)'
const ACTIVITY_COL_WIDTHS = [150, 150, 70, 140, 230, 230, 70]
const ACTIVITY_ROW_H = 44
const ACTIVITY_ROWS = [
  ['9/4/2026 13:44:24', '2,660.22082335 LIT', 'Unstake', STAKING_POOL, '0x8702A535AFA311722bA73a863f82ce910dcAeEff', 'ff95d7ad2a8722c070a199f844fb6f90e4a76d414a332292fc4041643b522bc2a0faec2b07611551'],
  ['9/4/2026 13:41:53', '1,140.88868064 LIT', 'Unstake', STAKING_POOL, '0x5170B09A9febBaFe7552d02631991A29195c6636', '2eb946ec7c87707bdd0244dec99750e6fb7881dc581ed42b13739e5338721ea172e881a3c8afc069'],
  ['9/4/2026 13:40:59', '93.11999785 LIT', 'Stake', '0x141a346C6E0CE0B2C13d19577a0e46250a99908f', STAKING_POOL, 'd0dc28717ec374cf6e34f19b122b2cc9578a0f2ee7e883e3b127e6b314bc02818dff41d742e63c1b'],
  ['9/4/2026 13:38:19', '750.1473528 LIT', 'Unstake', STAKING_POOL, '0x84A1c7d29661d783FF83F7DD65C1d89EC2e5689c', 'eb7e4d7db119d363b60373b11ec448dfb6d829c6b8f940f0152be2b9fd76704d5b7a039d91fa68e1'],
  ['9/4/2026 13:35:02', '9.99999573 LIT', 'Stake', '0x2e475CED9609398e71cdB2734E06A18D7176c077', STAKING_POOL, '8d5b084f9c21db2f1a1aea67d547c3594ee62b9e909b141b4801d00f5e14c70abae1f93a5eb205ac'],
  ['9/4/2026 13:34:11', '5,187.87923193 LIT', 'Unstake', STAKING_POOL, '0x58E6861Ac8037Be250D417831398b3DEe1f7787D', '7e221d304366081215ece0bb73a24e5fe94d241edc2634b05708af09f6c1b215d0e776f8aca934cb'],
  ['9/4/2026 13:33:42', '15,000.00000046 LIT', 'Unstake', STAKING_POOL, '0x8800a73bDEC0f3e38d05F385a93132c5061717ED', '5fcbb9cad3a28b80a1e827ce53752ecafe5c64b94cdddf5fab1d56fc71a57097a006f8d2f618c948'],
  ['9/4/2026 13:32:54', '1,000.01999723 LIT', 'Unstake', STAKING_POOL, '0x9fB0A115cB9a6D8dC3B254717c65AE4034e17a15', '3a17e23a3dee02c02a019c190cad8ef5e68090491479291efe890f3464e5ba11e5e253f494e27de8'],
  ['9/4/2026 13:30:48', '109.99272078 LIT', 'Unstake', STAKING_POOL, '0x7357dFE508E88d89C1a720CF5aDc27F326cdaC61', 'ef46dad242afc1219295ab6779a15dcc989f7fe08ed0978245dfc85b37b53e6fd19eca2c91b4fa0e'],
  ['9/4/2026 13:28:27', '35.62366462 LIT', 'Unstake', STAKING_POOL, '0x849dc7C44ad3d5ec819f06bc0609D167329b1bD4', '1870e495044147af156dd9cddf548cda998ad32aa6183b92fb254b5d81ada75f88ec118e9556fc85'],
  ['9/4/2026 13:28:17', '600.00000502 LIT', 'Unstake', STAKING_POOL, '0x5E0b351bADaDb5C0f190262b3e53153bFef38C0F', '4f688dc1ada0cf969b95f70697f79e35d1198ca8792ec8608221e54778214664abe9854854978e57'],
  ['9/4/2026 13:23:30', '1,073.66753418 LIT', 'Unstake', STAKING_POOL, 'dialch112.eth', '09246ac6a46c5b675919c94d3c06ac83f6262a7f645834abedb2d616a6dee11ebc13e6dc7ef63bc7'],
  ['9/4/2026 13:22:25', '10,436.23015588 LIT', 'Unstake', STAKING_POOL, '0x358661a9A0911351F15439171000d08BF0D324CD', '874000fef5d963895dea8c338bc6f47005910d3a0a26396589eb98b1bcacae77d38ba046c6a3a31f'],
  ['9/4/2026 13:21:20', '473.53999842 LIT', 'Stake', '0x2CB180fFa6D08551De4a49e06A98A28F6e76FB62', STAKING_POOL, '5a0bce20a31b27eb4879d2479e646e446a4e070b59e5cf7ba779b7105c9d5d7e4a2a73dc32756e26'],
  ['9/4/2026 13:17:03', '255.6100037 LIT', 'Stake', '0xC09108287381661c98834f354Bf9B0fA35FBCb22', STAKING_POOL, 'fc905d9657b522783018e284cc3f238b6502a842d48ecd87f912b73a207be3fe18cb1530dbbb120a'],
  ['9/4/2026 13:16:21', '95.80000088 LIT', 'Unstake', STAKING_POOL, '0x83664cC8F5c27F4Fa120008611e872427B1A2192', '63011e1348b5691af6b0c97997134e046da78cdaf7b5d566ade31d72eda300e64588cd503ec64feb'],
]

function buttonLabel(button) {
  const visible = button.querySelector('.col-start-1.row-start-1:not([aria-hidden])')
  if (visible?.textContent) return visible.textContent.replace(/\s+/g, ' ').trim()
  const first = button.querySelector(':scope > span')
  return (first?.textContent || button.textContent || '').replace(/\s+/g, ' ').trim()
}

function setTabPressed(button, pressed) {
  button.setAttribute('aria-pressed', pressed ? 'true' : 'false')
  button.className = pressed ? TAB_ACTIVE : TAB_IDLE
  const pill = [...button.children].find((el) => el.classList?.contains('absolute'))
  if (pressed && !pill) {
    const span = document.createElement('span')
    span.className = TAB_PILL
    span.setAttribute(
      'style',
      'filter: blur(0px); transform: none; transform-origin: 50% 50% 0px; opacity: 1'
    )
    button.insertBefore(span, button.firstChild)
  } else if (!pressed && pill) {
    pill.remove()
  }
}

function setPressedInBar(bar, label) {
  if (!bar) return
  bar.querySelectorAll('button[type="button"]').forEach((button) => {
    setTabPressed(button, buttonLabel(button) === label)
  })
}

function parseSvgMarkup(markup) {
  const template = document.createElement('template')
  template.innerHTML = markup.trim()
  return template.content.querySelector('svg')
}

function fitStakingChart(root = document) {
  const host = root.querySelector('.staking-page .staking-tvl-chart .echarts-for-react')
  let svg = host?.querySelector('svg')
  if (!host || !svg) return

  const mobile = window.matchMedia('(max-width: 767px)').matches
  const nextMode = mobile ? 'mobile' : 'desktop'

  if (!desktopTvlSvgHtml && svg.dataset.mobileChart !== '1') {
    desktopTvlSvgHtml = svg.outerHTML
  }

  let swapped = false
  if (stakingTvlChartMode !== nextMode) {
    if (mobile && svg.dataset.mobileChart !== '1') {
      const next = parseSvgMarkup(mobileTvlChartHtml)
      if (next) {
        next.dataset.mobileChart = '1'
        next.setAttribute('viewBox', '0 0 290 360')
        next.setAttribute('preserveAspectRatio', 'xMidYMid meet')
        next.setAttribute('overflow', 'visible')
        next.setAttribute(
          'style',
          svg.getAttribute('style') ||
            'position: absolute; left: 0px; top: 0px; user-select: none;'
        )
        svg.replaceWith(next)
        svg = next
        swapped = true
      }
    } else if (!mobile && svg.dataset.mobileChart === '1' && desktopTvlSvgHtml) {
      const next = parseSvgMarkup(desktopTvlSvgHtml)
      if (next) {
        svg.replaceWith(next)
        svg = next
        swapped = true
      }
    }
    stakingTvlChartMode = nextMode
    if (swapped) {
      host.dispatchEvent(new CustomEvent('staking-tvl-chart-swapped'))
    }
  }

  const width = Number.parseFloat(svg.getAttribute('width')) || (mobile ? 290 : 585)
  const height = Number.parseFloat(svg.getAttribute('height')) || 360
  if (!svg.getAttribute('viewBox')) {
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`)
  }
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet')
  svg.style.overflow = 'visible'

  const frame = host.querySelector(':scope > div')
  if (frame) {
    frame.style.overflow = 'visible'
    frame.style.height = '360px'
    if (mobile) {
      frame.style.width = '100%'
      frame.style.maxWidth = '100%'
    } else {
      frame.style.width = '585px'
      frame.style.maxWidth = '585px'
    }
  }

  if (!mobile) {
    svg.setAttribute('width', '585')
    svg.setAttribute('height', '360')
    svg.style.width = '585px'
    svg.style.height = '360px'
  }
}

const TAB_PILL_STYLE =
  'filter: blur(0px); transform: none; transform-origin: 50% 50% 0px; opacity: 1'

function feeStatCard(label, clipId) {
  return `<div class="flex flex-col gap-1 rounded-card p-4 border-[0.5px] border-line bg-fill shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under)] light:bg-surface light:shadow-[inset_0_0_0_0.5px_var(--ring-crisp)]"><span class="flex items-center gap-1 text-sm text-muted">${label}${feeInfoIcon(clipId)}</span><span class="text-3xl font-medium text-ink tabular-nums">-</span></div>`
}

function feeCreditsPanel() {
  return `<div><div class="flex flex-1 flex-col gap-5 p-4 max-mobile:gap-3 max-mobile:p-3"><p class="m-0 max-w-[64ch] text-md/xl text-dim">LIT Fee Credits allow traders to temporarily access a selected fee/latency tier without staking tokens. To activate a tier, traders must acquire LIT, specify their desired effective staked LIT amount and duration, and pay an upfront fee in LIT. The selected tier becomes active immediately upon payment. All LIT paid for Fee Credits is allocated to stakers as rewards.</p><div class="grid grid-cols-3 gap-3 max-mobile:grid-cols-1 max-mobile:gap-2">${feeStatCard('Credited in LIT', 'fee-clip-credited')}${feeStatCard('Effective LIT', 'fee-clip-effective')}${feeStatCard('Current Tier', 'fee-clip-tier')}</div><div class="flex flex-col gap-2"><div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1"><span class="font-mono label-meta text-muted">Next tier</span><span class="text-sm text-muted tabular-nums">-</span></div><div class="h-1.5 overflow-hidden rounded-full bg-ink/8"><div class="h-full rounded-full bg-up-strong" style="width: 0%;"></div></div><p class="m-0 text-sm text-muted">Current Tier: -</p></div></div></div>`
}

function feeCreditsExtraHtml() {
  return `<div class="overflow-hidden rounded-panel border-[0.5px] border-line bg-fill max-mobile:border-0 max-mobile:bg-transparent"><div class="card-header border-b-[0.5px] border-line flex min-h-(--control-h-xl) items-center px-2 [&>*]:w-full"><div class="flex flex-wrap items-center justify-between gap-3"><h3 class="m-0 text-sm font-medium text-ink">Your Fee Credits</h3><div class="flex items-stretch gap-1.5" data-fee-credit-filters>${['All', 'Active', 'Expired']
    .map(
      (name, index) =>
        `<button type="button" aria-pressed="${index === 0}" class="${index === 0 ? TAB_ACTIVE : TAB_IDLE}">${
          index === 0
            ? `<span class="${TAB_PILL}" style="${TAB_PILL_STYLE}"></span>`
            : ''
        }<span class="relative z-1 grid place-items-center"><span class="col-start-1 row-start-1 flex items-center gap-1.5">${name}</span><span aria-hidden="true" class="invisible col-start-1 row-start-1 flex items-center gap-1.5 font-medium">${name}</span></span></button>`
    )
    .join('')}</div></div></div>${positionsEmptyHtml({ connected: false, tab: 'Fee Credits' })}</div>`
}

function activityAccount(text) {
  return `<button class="inline-flex items-center gap-2 text-sm/sm font-medium disabled:opacity-50 text-link hover:opacity-80 active:opacity-60 overflow-hidden"><span class="overflow-hidden pr-2 text-left text-sm text-ellipsis">${text}</span></button>`
}

function shortenAccount(text) {
  if (/^0x[a-fA-F0-9]{40}$/i.test(text)) {
    return `${text.slice(0, 6)}...${text.slice(-4)}`
  }
  return text
}

function formatTimeAgo(dateStr, now = Date.now()) {
  const then = Date.parse(dateStr)
  if (Number.isNaN(then)) return dateStr
  const sec = Math.max(0, Math.floor((now - then) / 1000))
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  return `${day}d ago`
}

const ACTIVITY_CARD_CLASS =
  'flex w-full flex-col gap-3 overflow-hidden rounded-panel p-4 text-left text-base text-ink border-[0.5px] border-line bg-fill shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under)] light:bg-surface light:shadow-[inset_0_0_0_0.5px_var(--ring-crisp)]'

function activityMobileRow(label, valueHtml) {
  return `<div class="flex w-full items-center justify-between gap-4"><div class="font-mono label-meta text-muted shrink-0">${label}</div><span class="min-w-0 truncate text-right text-sm text-ink *:max-w-full *:min-w-0">${valueHtml}</span></div>`
}

function stakingActivityPanel() {
  const headers = [
    ['Date', 150],
    ['Amount', 150],
    ['Fee', 70],
    ['Type', 140],
    ['From Account', 230],
    ['To Account', 230],
    ['Explorer', 70],
  ]
  const keys = ['timestamp', 'amount', 'fee', 'type', 'fromAccountL1', 'toAccountL1', 'explorer']
  const bodyH = 4400

  const rows = ACTIVITY_ROWS.map(([date, amount, type, from, to, hash], index) => {
    const stripe = index % 2 === 0 ? ' bg-ink/4' : ''
    const typeClass = type === 'Stake' ? 'text-up' : 'text-down'
    const cells = [
      date,
      amount,
      '-',
      `<span class="font-mono tracking-caps uppercase ${typeClass}">${type}</span>`,
      activityAccount(from),
      activityAccount(to),
      `<a href="/explorer/logs/${hash}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 text-sm/sm text-link underline decoration-link/75 underline-offset-2 transition-colors hover:decoration-link">Explorer${EXPLORER_ICON}</a>`,
    ]
    return `<tr data-index="${index}" data-testid="row-${index}" class="absolute flex w-full items-center max-mobile:px-1 min-h-[44px] gap-3 px-3 text-base/md text-ink tabular-nums${stripe}" style="transform: translateY(${index * ACTIVITY_ROW_H}px);">${cells
      .map((content, col) => {
        const grow =
          col === 0
            ? 'flex grow items-center self-stretch'
            : col === 4 || col === 5
              ? 'flex grow overflow-hidden'
              : 'flex grow'
        return `<td data-testid="row-${index}-cell-${index}_${keys[col]}" class="${grow}" style="width: ${ACTIVITY_COL_WIDTHS[col]}px;">${content}</td>`
      })
      .join('')}</tr>`
  }).join('')

  const cardsNow = Math.max(Date.now(), ...ACTIVITY_ROWS.map(([d]) => Date.parse(d))) + 60_000
  const cards = ACTIVITY_ROWS.map(([date, amount, type, from], index) => {
    const typeClass = type === 'Stake' ? 'text-up' : 'text-down'
    return `<div data-index="${index}" class="${ACTIVITY_CARD_CLASS}"><div class="flex flex-col gap-1 text-md text-ink"><div class="min-w-0"><span class="font-mono text-dim">${shortenAccount(from)}</span></div></div>${activityMobileRow('Action', `<span class="font-mono tracking-caps uppercase ${typeClass}">${type}</span>`)}${activityMobileRow('Amount', amount)}${activityMobileRow('Time', `<span class="text-muted">${formatTimeAgo(date, cardsNow)}</span>`)}</div>`
  }).join('')

  return `<div class="flex flex-1 flex-col overflow-scroll max-mobile:h-[unset]"><div class="staking-activity-desktop" data-auto-sizer=""><div class="relative size-full overflow-auto" style="height: 448px;"><table data-testid="transfer-history-table" class="relative z-0 grid text-xs whitespace-nowrap text-ink bg-transparent"><thead class="sticky top-0 z-5 grid align-middle border-b-[0.5px] border-line [background:linear-gradient(var(--color-fill),var(--color-fill)),var(--color-bg)]"><tr class="flex w-full items-center gap-3 px-3 h-9 min-h-0">${headers
    .map(
      ([label, width]) =>
        `<th class="flex items-center py-1 leading-2xs font-normal whitespace-nowrap uppercase font-mono label-meta text-muted grow min-w-0" role="columnheader" style="width: ${width}px;">${label}</th>`
    )
    .join(
      ''
    )}</tr></thead><tbody class="relative grid" style="height: ${bodyH}px;">${rows}</tbody><tfoot><tr class="flex w-full"><td class="w-full" colspan="7"><div aria-hidden="true" class="h-2"></div></td></tr></tfoot></table></div></div><div class="staking-activity-mobile p-2.5"><div class="size-full"><div class="flex flex-col gap-2">${cards}</div></div></div></div>`
}

const ABOUT_HTML = `<p class="m-0 text-md/xl text-dim">The LIT Staking Pool is designed to align long-term participants with the core economics of the protocol.</p>`

export default function Staking() {
  const [headerContainer, setHeaderContainer] = useState(null)
  const [amountModal, setAmountModal] = useState(null)
  const [tvlFilterOpen, setTvlFilterOpen] = useState(false)
  const [tvlPeriod, setTvlPeriod] = useState('1W')
  const tvlFilterAnchorRef = useRef(null)
  usePageCss('staking-page-css', '/css/lighter_staking.css')

  useEffect(() => {
    const container = document.getElementById('header-placeholder')
    if (container) {
      setHeaderContainer(container)
    }
  }, [])

  useEffect(() => {
    const signalHost = {}
    const detailsContent = document.querySelector(
      '[data-testid="staking-pool-performance-content"]'
    )
    const detailsHtml = detailsContent?.innerHTML ?? ''
    const bottomPanel = document.getElementById('staking-bottom-panel')
    const rewardsHtml = bottomPanel?.innerHTML ?? ''
    const bottomCard = bottomPanel?.parentElement
    const detailsBar = document.querySelector(
      '[data-testid="staking-pool-performance-tabs"]'
    )
    const bottomBar = bottomPanel?.previousElementSibling

    const removeFeeCreditsExtra = () => {
      bottomCard?.parentElement
        ?.querySelector('[data-fee-credits-extra]')
        ?.remove()
      if (bottomPanel) bottomPanel.className = BOTTOM_PANEL_CLASS
    }

    const showFeeCreditsExtra = () => {
      if (!bottomCard || !bottomPanel) return null
      bottomPanel.className = BOTTOM_PANEL_FEE_CLASS
      const wrap = document.createElement('div')
      wrap.className = 'mt-3'
      wrap.setAttribute('data-fee-credits-extra', '')
      wrap.innerHTML = feeCreditsExtraHtml()
      bottomCard.after(wrap)
      return wrap
    }

    const showBottomTab = (label) => {
      setPressedInBar(bottomBar, label)
      if (!bottomPanel) return
      stopPositionsSignal(signalHost)
      removeFeeCreditsExtra()
      if (label === 'Rewards') {
        bottomPanel.innerHTML = rewardsHtml
      } else if (label === 'LIT Fee Credits') {
        bottomPanel.innerHTML = feeCreditsPanel()
        const extra = showFeeCreditsExtra()
        startPositionsSignal(signalHost, extra || bottomPanel)
      } else if (label === 'Staking Activity') {
        bottomPanel.innerHTML = stakingActivityPanel()
      } else if (label === 'My Activity') {
        bottomPanel.innerHTML = positionsEmptyHtml({
          connected: false,
          tab: 'My Activity',
        })
        startPositionsSignal(signalHost, bottomPanel)
      } else if (label === 'Pending Unstakes') {
        bottomPanel.innerHTML = positionsEmptyHtml({
          connected: false,
          tab: 'Pending Unstakes',
        })
        startPositionsSignal(signalHost, bottomPanel)
      } else {
        bottomPanel.innerHTML = positionsEmptyHtml({
          connected: false,
          tab: 'My Activity',
        })
        startPositionsSignal(signalHost, bottomPanel)
      }
    }

    const onClick = (event) => {
      const connectTarget = event.target.closest(
        '[data-testid="connect-wallet-button"], .cursor-pointer.underline'
      )
      if (connectTarget) {
        event.preventDefault()
        window.dispatchEvent(new Event('lighter:open-connect'))
        return
      }

      const dismissTarget = event.target.closest(
        '[aria-label="Dismiss announcement"]'
      )
      if (dismissTarget) {
        event.preventDefault()
        const banner = dismissTarget.closest('[style*="grid-template-rows"]')
        if (banner) banner.style.gridTemplateRows = '0fr'
        return
      }

      const copyTarget = event.target.closest('[aria-label="Copy"]')
      if (copyTarget) {
        event.preventDefault()
        navigator.clipboard?.writeText('0x0000000000000000000000000000000000000000')
        return
      }

      const filterBtn = event.target.closest(
        '.staking-page button[aria-label="Filter By"]'
      )
      if (filterBtn) {
        event.preventDefault()
        tvlFilterAnchorRef.current = filterBtn
        setTvlFilterOpen((open) => !open)
        return
      }

      const button = event.target.closest('button')
      if (!button) return
      const label = buttonLabel(button)

      if (detailsBar?.contains(button) && (label === 'Details' || label === 'About')) {
        event.preventDefault()
        setPressedInBar(detailsBar, label)
        if (detailsContent) {
          detailsContent.innerHTML = label === 'About' ? ABOUT_HTML : detailsHtml
        }
        return
      }

      const feeFilters = event.target.closest('[data-fee-credit-filters]')
      if (feeFilters?.contains(button)) {
        event.preventDefault()
        setPressedInBar(feeFilters, label)
        return
      }

      if (bottomBar?.contains(button)) {
        event.preventDefault()
        showBottomTab(label)
        return
      }

      if (label === 'Stake' || label === 'Unstake') {
        event.preventDefault()
        setAmountModal(label)
        return
      }

      if (label === 'LIT Fee Credits') {
        event.preventDefault()
        showBottomTab('LIT Fee Credits')
      }
    }

    document.addEventListener('click', onClick)
    fitStakingChart()
    let stopTvlChart = startTvlChartInteraction()
    const chartHost = document.querySelector(
      '.staking-page .staking-tvl-chart .echarts-for-react'
    )
    const restartTvlChart = () => {
      stopTvlChart()
      stopTvlChart = startTvlChartInteraction()
    }
    chartHost?.addEventListener('staking-tvl-chart-swapped', restartTvlChart)
    const onResize = () => fitStakingChart()
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('click', onClick)
      window.removeEventListener('resize', onResize)
      chartHost?.removeEventListener('staking-tvl-chart-swapped', restartTvlChart)
      stopPositionsSignal(signalHost)
      stopTvlChart()
      stakingTvlChartMode = null
      desktopTvlSvgHtml = null
    }
  }, [])

  useEffect(() => {
    const btn = document.querySelector(
      '.staking-page button[aria-label="Filter By"]'
    )
    if (btn) tvlFilterAnchorRef.current = btn
  }, [])

  useEffect(() => {
    const btn = tvlFilterAnchorRef.current
    if (!btn) return
    btn.setAttribute('aria-expanded', tvlFilterOpen ? 'true' : 'false')
    btn.setAttribute('data-state', tvlFilterOpen ? 'open' : 'closed')
  }, [tvlFilterOpen])

  useEffect(() => {
    if (!tvlFilterOpen) return undefined
    const onPointer = (event) => {
      if (
        event.target.closest(
          '[data-testid="staking-chart-filter-menu"], .staking-page button[aria-label="Filter By"]'
        )
      ) {
        return
      }
      setTvlFilterOpen(false)
    }
    const onKey = (event) => {
      if (event.key === 'Escape') setTvlFilterOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      window.removeEventListener('keydown', onKey)
    }
  }, [tvlFilterOpen])

  return (
    <>
      <StakingMarkup />
      {headerContainer && createPortal(<Header />, headerContainer)}
      {tvlFilterOpen ? (
        <TvlFilterMenu
          anchorRef={tvlFilterAnchorRef}
          period={tvlPeriod}
          onPeriod={(next) => {
            setTvlPeriod(next)
            setTvlFilterOpen(false)
          }}
        />
      ) : null}
      {amountModal &&
        createPortal(
          <AmountModal
            title={amountModal}
            onClose={() => setAmountModal(null)}
          />,
          document.body
        )}
    </>
  )
}

function AmountModal({ title, onClose }) {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <section>
      <div
        className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none">
        <div
          role="dialog"
          aria-labelledby="staking-amount-modal-title"
          className="pointer-events-auto w-[420px] max-w-full overflow-hidden rounded-panel border-[0.5px] border-line bg-fill shadow-[0_24px_80px_rgba(0,0,0,0.55)]"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-3 border-b-[0.5px] border-line px-4 py-3">
            <h2
              id="staking-amount-modal-title"
              className="m-0 text-lg font-medium text-ink"
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex size-7 items-center justify-center rounded-pill text-dim transition-colors hover:bg-ink/10 hover:text-ink"
              aria-label="Close"
            >
              <span data-icon="true" className="flex size-4 items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className="size-full">
                  <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" />
                </svg>
              </span>
            </button>
          </div>
          <div className="flex flex-col gap-4 p-4">
            <label className="flex flex-col gap-2">
              <span className="text-md text-dim">Amount</span>
              <input
                type="text"
                placeholder="0.00 LIT"
                className="h-12 rounded-card border-[0.5px] border-line bg-bg px-3 text-md text-ink outline-none placeholder:text-dim"
              />
            </label>
            <div className="flex flex-col gap-2 text-md">
              <div className="flex items-center justify-between gap-4">
                <span className="text-dim">Available to unstake</span>
                <span className="text-ink">-</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-dim">Min. Staking Req.</span>
                <span className="text-ink">-</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-dim">Staking Pool Lockup Period</span>
                <span className="text-up underline decoration-up/50 underline-offset-2">
                  3 days
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event('lighter:open-connect'))}
              className="group inline-flex h-12 w-full cursor-pointer items-center justify-center truncate rounded-pill bg-ink px-3.5 text-md font-medium text-bg shadow-border-button-primary hover:bg-ink/90 active:bg-ink/80"
            >
              Connect Wallet to Trade
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
